import { keys } from "./keys";

/**
 * Strongly typed replacement for `Object.entries`.
 *
 * Unlike the built-in `Object.entries`, which returns `[string, any][]`,
 * this function preserves both the keys and values of the given object
 * as strongly typed tuples. This allows TypeScript to infer the exact
 * key union and corresponding value types.
 *
 * @typeParam T - The object type, constrained to `Record<string, unknown>`.
 * @param obj - The object whose own enumerable property `[key, value]` pairs are to be returned.
 * @returns An array of `[key, value]` tuples, typed as `[keyof T, T[keyof T]][]`.
 *
 * @example
 * type Locale = "en" | "no";
 * const allLocales: Record<Locale, string> = { en: "English", no: "Norsk" };
 *
 * const localeEntries = entries(allLocales);
 * // type is [Locale, string][]
 *
 * for (const [locale, label] of localeEntries) {
 *   // locale is strongly typed as "en" | "no"
 *   // label is string
 * }
 */

export const entries = <T extends Record<string, unknown>>(
  obj: T,
): [keyof T, T[keyof T]][] => keys(obj).map((k) => [k, obj[k]]);
